import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Capacitor } from '@capacitor/core';
import { OAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth } from './index';
import { markOAuthRedirectPending } from './completeOAuthRedirect';
import { isEmbeddedBrowser } from './oauthEnv';

export const signInWithApplePopup = async (): Promise<{
  idToken: string | null;
}> => {
  try {
    if (Capacitor.isNativePlatform()) {
      await FirebaseAuthentication.signInWithApple();
      // Same reasoning as signInWithGooglePopup: the backend verifies a
      // Firebase-issued ID token, not Apple's own OAuth credential.
      const { token } = await FirebaseAuthentication.getIdToken();

      if (!token) {
        throw new Error('No idToken returned from Apple sign-in');
      }

      return { idToken: token };
    }

    // In-app browsers (Instagram, Telegram…) can't open popups: full-page
    // redirect, completed by completeOAuthRedirect() on the next load.
    if (isEmbeddedBrowser()) {
      markOAuthRedirectPending('apple');
      await FirebaseAuthentication.signInWithApple({ mode: 'redirect' });
      // The line above navigates away, so this is never reached.
      return { idToken: null };
    }

    // Every other browser, iPhone Safari included: popup straight from the
    // Firebase SDK (see google.ts — the redirect via firebaseapp.com loses its
    // result, and the plugin's lazy web code gets the popup blocked on iOS).
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    const credential = await signInWithPopup(auth, provider);
    const token = await credential.user.getIdToken();

    if (!token) {
      throw new Error('No idToken returned from Apple sign-in');
    }

    return { idToken: token };
  } catch (error) {
    console.error('Error during Apple sign-in:', error);
    throw error;
  }
};
